import React, { useState, useEffect } from "react";
import { Clock, Trash2, AlertTriangle } from "lucide-react";
import { Modal } from "./Modal";
import { SaveName } from "./SaveName";

// Shared "Meus salvamentos" block. Optional: onRename(save, name), rowValue(save) -> {text, over},
// renderItems(save) may return JSX using the live save object (re-rendered after patches).
export const MySaves = ({ saves, onDelete, onRename, rowValue, lang, t, testid, itemLabel, renderItems, initialViewId, bare, onCloseView }) => {
  const [viewId, setViewId] = useState(null);
  useEffect(() => { if (initialViewId && saves.some((s) => s.save_id === initialViewId)) setViewId(initialViewId); }, [initialViewId, saves]);
  const view = saves.find((s) => s.save_id === viewId) || null;
  useEffect(() => { if (viewId && !view) setViewId(null); }, [viewId, view]);
  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(lang, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  return (
    <div className={bare ? "" : "glass mt-6 rounded-2xl p-6"} data-testid={testid}>
      <div className="mb-1 flex items-center gap-2">
        <Clock className="h-4 w-4 text-xmas-gold" />
        <h3 className="font-display text-sm text-xmas-snow">{t("saves.title")}</h3>
        <span className="text-[11px] text-xmas-slate">({saves.length}/3)</span>
      </div>
      <p className="mb-3 text-xs text-xmas-slate">{t("saves.hint")}</p>
      {saves.length === 0 ? (
        <p className="text-xs text-xmas-slate">{t("saves.empty")}</p>
      ) : (
        <div className={bare ? "space-y-2" : "grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3"}>
          {saves.map((s) => {
            const rv = rowValue ? rowValue(s) : null;
            return (
              <div key={s.save_id} data-testid={`${testid}-${s.save_id}`} className="flex items-center justify-between gap-2 rounded-xl bg-xmas-navy/40 px-3 py-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <button onClick={() => setViewId(s.save_id)} data-testid={`${testid}-view-${s.save_id}`} aria-label="view" className="shrink-0"><Clock className="h-3.5 w-3.5 text-xmas-gold" /></button>
                  {onRename
                    ? <SaveName name={s.name} fallback={fmtDate(s.saved_at)} placeholder={t("saves.name_ph")} testid={`${testid}-name-${s.save_id}`} onClick={() => setViewId(s.save_id)} onRename={(v) => onRename(s, v)} />
                    : <button onClick={() => setViewId(s.save_id)} className="min-w-0 flex-1 truncate text-left text-sm text-xmas-snow">{s.name || fmtDate(s.saved_at)}</button>}
                  {s.name && <span className="shrink-0 text-[11px] text-xmas-slate" data-testid={`${testid}-date-${s.save_id}`}>{fmtDate(s.saved_at)}</span>}
                  <button onClick={() => setViewId(s.save_id)} className={`inline-flex shrink-0 items-center gap-1 font-mono-num text-xs ${rv?.over ? "text-xmas-crimson" : "text-xmas-champagne"}`} data-testid={`${testid}-value-${s.save_id}`}>
                    {rv?.over && <AlertTriangle className="h-3 w-3" />}{rv ? rv.text : `${s.count} ${itemLabel}`}
                  </button>
                </div>
                <button onClick={() => onDelete(s.save_id)} data-testid={`${testid}-delete-${s.save_id}`} aria-label="delete" className="shrink-0 rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <Modal open={!!view} onClose={() => { setViewId(null); onCloseView?.(); }} title={view ? `${view.name || t("saves.items")} · ${fmtDate(view.saved_at)}` : ""} testid={`${testid}-view-modal`}>
        {view && renderItems(view)}
      </Modal>
    </div>
  );
};
